import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {EnvironmentService} from '@app/services/environment.service';
import {HttpClient} from '@angular/common/http';
import {SizeElasticityReport} from '@app/models/size-elasticity-report.model';
import {ModelCacheUtil} from "@app/utils/model-cache-util";
import {UtilService} from '@app/services/util.service';

@Injectable({
  providedIn: 'root'
})
export class SizeElasticityService {

  /**
   * Cached list of size reports
   */
  private cache: ModelCacheUtil<SizeElasticityReport>;

  constructor(private http: HttpClient, private environmentService: EnvironmentService, private utilService: UtilService) {
    this.cache = new ModelCacheUtil<SizeElasticityReport>();
  }

  get sizeElasticityReports(): Array<SizeElasticityReport> {
    return this.cache.cachedModels;
  }

  getNewReportName(): string {
    const cachedReports = this.cache.cachedModels;
    const prependName = 'Untitled Size Elasticity';
    let count = 0;
    let newName = `${prependName} ${count}`;
    let loop = true;
    while (loop) {
      if (cachedReports.find((report: SizeElasticityReport) => {
        return report.name.toLowerCase().trim() === newName.toLowerCase().trim();
      })) {
        newName = `${prependName} ${++count}`;
      } else {
        loop = false;
      }
    }
    return newName;
  }

  getDuplicateReportName(prependName: string, reportNameMaxLength: number): string {
    const cachedReports = this.cache.cachedModels;
    return this.utilService.generateDuplicateReportName(prependName, reportNameMaxLength, cachedReports);
  }

  fetchAll(projectId: number, modelRunId: string): Observable<Array<SizeElasticityReport>> {
    const env = this.environmentService.environment.authProxy;
    const url = `${env.url}/${env.lpoSimulatorContextPath}/projects/${projectId}/runs/${modelRunId}/sizeElasticityReports`;
    return this.http.get<Array<SizeElasticityReport>>(url).pipe(map((multipleReports: SizeElasticityReport[]) => {
      this.cache.appendAll(multipleReports);
      return multipleReports;
    }));
  }

  /**
   * Lists all Size elasticity reports associated with given project and run id.
   */
  getAll(projectId: number, modelRunId: string): Observable<Array<SizeElasticityReport>> {
    const reports = this.cache.cachedModels.filter((it: SizeElasticityReport) => {
      return it.projectId === projectId && it.modelRunId === modelRunId;
    });
    if (reports.length) {
      return of(reports);
    } else {
      return this.fetchAll(projectId, modelRunId);
    }
  }

  /**
   * Returns single Size elasticity report associated with given project and run id.
   */
  getById(id: string, params: { projectId: number; modelRunId: string }): Observable<SizeElasticityReport> {
    const report = this.cache.cachedModels.find((it: SizeElasticityReport) => {
      return it.id === id;
    });
    if (report) {
      return of(report);
    } else {
      const env = this.environmentService.environment.authProxy;
      const contextPath = `${env.url}/${env.lpoSimulatorContextPath}`;
      const url = `${contextPath}/projects/${params.projectId}/runs/${params.modelRunId}/sizeElasticityReports/${id}`;
      return this.http.get<SizeElasticityReport>(url).pipe(map((singleReport: SizeElasticityReport) => {
        this.cache.append(singleReport);
        return singleReport;
      }));
    }
  }

  /**
   * Generates Size elasticity report associated with given project and run id.
   */
  add(sizeElasticityReport: SizeElasticityReport): Observable<SizeElasticityReport> {
    const env = this.environmentService.environment.authProxy;
    const url = `${env.url}/${env.lpoSimulatorContextPath}/projects/${sizeElasticityReport.projectId}/runs/${sizeElasticityReport.modelRunId}/sizeElasticityReports`;
    return this.http.post<SizeElasticityReport>(url, {sizeElasticityReport}).pipe(map((singleReport: SizeElasticityReport) => {
      this.cache.append(singleReport);
      return singleReport;
    }));
  }

  delete(sizeElasticityReport: SizeElasticityReport): Observable<string> {
    const env = this.environmentService.environment.authProxy;
    const projectId = sizeElasticityReport.projectId;
    const modelRunId = sizeElasticityReport.modelRunId;
    const id = sizeElasticityReport.id;
    const contextPath = `${env.url}/${env.lpoSimulatorContextPath}`;
    const url = `${contextPath}/projects/${projectId}/runs/${modelRunId}/sizeElasticityReports/${id}`;
    return this.http.delete<SizeElasticityReport>(url).pipe(map(() => {
      this.cache.remove(sizeElasticityReport);
      return sizeElasticityReport.id;
    }));
  }

  /**
   * Method is use to download file.
   * @param data - Array Buffer data
   * @param type - type of the document.
   */
  generateReport(report: SizeElasticityReport): Observable<any> {
    const env = this.environmentService.environment.authProxy;
    const projectId = report.projectId;
    const modelRunId = report.modelRunId;
    const id = report.id;
    const contextPath = `${env.url}/${env.lpoSimulatorContextPath}`;
    const url = `${contextPath}/projects/${projectId}/runs/${modelRunId}/sizeElasticityReports/${id}/download`;
    return this.http.get(`${url}`, {
        responseType: 'arraybuffer'
      }
    );
  }

  /**
   * Method is used to generate Power BI report
   */
  powerBIReportGeneration(report: SizeElasticityReport): Observable<any> {
    const env = this.environmentService.environment.authProxy;
    const projectId = report.projectId;
    const modelRunId = report.modelRunId;
    const id = report.id;
    const contextPath = `${env.url}/${env.lpoSimulatorContextPath}`;
    const url = `${contextPath}/projects/${projectId}/runs/${modelRunId}/sizeElasticityReports/${id}/powerBIReportGeneration`;
    return this.http.get(url);
  }

  refreshCachedModel(updatedData: any): SizeElasticityReport {
    if (updatedData) {
      const updatedModel = <SizeElasticityReport>updatedData;
      return this.cache.refresh(updatedModel);
    }
    return null;
  }
}
